/**
 * Block IDs + cozy palette for meshing.
 * 0 = air (non-solid). Solids are opaque for greedy face culling.
 * Phase 2.15: tan packed-earth paths; blue stream/pond beds (ground only, no fluid sim);
 * thicker trunks / flatter rocks; bent tall-grass mesh.
 */

export const BLOCK = {
  AIR: 0,
  /** Grasslands surface — bright meadow green. */
  GRASS: 1,
  DIRT: 2,
  STONE: 3,
  SAND: 4,
  /**
   * Static sea / puddle fill (walk-through). NOT a dynamic fluid —
   * no flow, pressure, or physics sim yet.
   */
  WATER: 5,
  /** Oak-ish trunk (grasslands / general). */
  LOG: 6,
  /** Broadleaf canopy. */
  LEAVES: 7,
  /**
   * Decorative grass — walk-through. Meshed as half-size blades
   * (custom pass: straight + bent varieties), not full greedy cubes.
   */
  TALL_GRASS: 8,
  /** Forest floor — deeper green. */
  FOREST_GRASS: 9,
  /** Swamp surface — deep dark green (playtest 2.10). */
  SWAMP_GRASS: 10,
  /** Rock-fields surface / boulders — cooler grey. */
  ROCK: 11,
  /** Conifer trunk. */
  PINE_LOG: 12,
  /** Needle canopy — blue-green. */
  PINE_LEAVES: 13,
  /** Swamp willow trunk — greyer bark. */
  WILLOW_LOG: 14,
  /** Swamp canopy — yellow-green. */
  WILLOW_LEAVES: 15,
  /** Swamp muck under surface. */
  MUD: 16,
  /**
   * Packed trail / road surface.
   * Playtest 2.15: warm tan packed earth (roads read on all biomes).
   */
  PATH: 17,
  /**
   * Stream bed marker — solid light-blue ground (playtest 2.9 distinguish).
   * Not fluid; walkable solid for now.
   */
  STREAM_BED: 18,
  /**
   * Pond bed marker — solid dark-blue ground (playtest 2.9 distinguish).
   * Rocks never spawn on ponds (2.8+); roads must not either.
   */
  POND_BED: 19,
  /**
   * Building / ruin site placeholder pad (playtest 2.12).
   * Marks oblong/kidney settlement footprints near roads — not final art.
   */
    SITE_PAD: 20,
    /**
     * Road×stream bridge deck (playtest 2.21).
     * Flat solid sheet at approach height over STREAM_BED — not a full mesh prop.
     */
    BRIDGE: 21,
  /**
   * Indestructible floor at world Y=0 (full rescale Phase 2.5+).
   * Column law: bedrock → dirt band → surface; no dig below y0.
   */
  BEDROCK: 22,
} as const;

export type BlockId = (typeof BLOCK)[keyof typeof BLOCK];

/** RGB 0–1 for vertex colors / flat materials. */
export const BLOCK_COLOR: Record<number, readonly [number, number, number]> = {
  [BLOCK.AIR]: [0, 0, 0],
  [BLOCK.GRASS]: [0.55, 0.78, 0.52],
  [BLOCK.DIRT]: [0.62, 0.48, 0.34],
  [BLOCK.STONE]: [0.62, 0.66, 0.7],
  // Playtest 2.19: aqua sand (low/beach — was warm tan, read as bare dirt)
  [BLOCK.SAND]: [0.42, 0.82, 0.78],
  [BLOCK.WATER]: [0.45, 0.7, 0.88],
  [BLOCK.LOG]: [0.48, 0.32, 0.2],
  [BLOCK.LEAVES]: [0.42, 0.72, 0.4],
  [BLOCK.TALL_GRASS]: [0.48, 0.82, 0.38],
  [BLOCK.FOREST_GRASS]: [0.32, 0.58, 0.36],
  // Playtest 2.10: deep dark green swamp (was olive 0.40/0.52/0.34)
  [BLOCK.SWAMP_GRASS]: [0.12, 0.32, 0.14],
  [BLOCK.ROCK]: [0.55, 0.56, 0.6],
  [BLOCK.PINE_LOG]: [0.38, 0.28, 0.22],
  [BLOCK.PINE_LEAVES]: [0.28, 0.52, 0.42],
  [BLOCK.WILLOW_LOG]: [0.42, 0.4, 0.32],
  [BLOCK.WILLOW_LEAVES]: [0.38, 0.55, 0.28],
  // Darker muck so swamp underfoot stays muddy, not tan
  [BLOCK.MUD]: [0.28, 0.26, 0.18],
  // Warm tan packed earth (playtest 2.15 — overlay readable, not debug-red)
  [BLOCK.PATH]: [0.82, 0.68, 0.42],
  // Playtest 2.9: stream light blue / pond dark blue so beds read at a glance
  [BLOCK.STREAM_BED]: [0.42, 0.72, 0.98],
  [BLOCK.POND_BED]: [0.12, 0.28, 0.62],
  // Playtest 2.13: bright pink pad so building/ruin sites read at a glance
  [BLOCK.SITE_PAD]: [0.95, 0.35, 0.72],
  // Playtest 2.21: warm wood bridge deck (reads vs tan PATH + blue beds)
  [BLOCK.BRIDGE]: [0.62, 0.42, 0.28],
  // Full rescale: dark indestructible floor
  [BLOCK.BEDROCK]: [0.22, 0.2, 0.24],
};

/**
 * Collision solids. Water + tall grass are walk-through.
 * Stream/pond beds are solid placeholders (ground paint).
 */
export function isSolid(id: number): boolean {
  return id !== BLOCK.AIR && id !== BLOCK.WATER && id !== BLOCK.TALL_GRASS;
}

/**
 * Indestructible ids — full rescale y=0 bedrock floor cannot be mined or overwritten
 * via gameplay setBlock. Worldgen may still paint BEDROCK at column base.
 */
export function isIndestructible(id: number): boolean {
  return id === BLOCK.BEDROCK;
}

/**
 * Greedy mesher opacity. Water + tall grass are non-opaque so banks/ground
 * show through; grass faces come from a half-size custom pass in mesher.
 */
export function isOpaque(id: number): boolean {
  return id !== BLOCK.AIR && id !== BLOCK.WATER && id !== BLOCK.TALL_GRASS;
}
